import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import PricingSection, {
  VALID_PLAN_IDS,
  BACKEND_PLAN_ID_TO_MARKETING,
  isValidBillingCycle,
} from '@/components/PricingSection';
import BrandLogo from '@/components/BrandLogo';
import { subscription } from '@/lib/api';
import AppFooter from '@/components/AppFooter';
import PlanUsageComparison from '@/components/subscription/PlanUsageComparison';

// Subscription statuses that mean the account already has a paid plan and
// should land on the subscription management page (with cancel / upgrade /
// downgrade actions) rather than the marketing plans page.
const SUBSCRIBED_STATUSES = ['active', 'past_due', 'cancelled'];

export default function BillingPlans() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        // The subscription belongs to the school-admin account, not a school:
        // read the account-level status (billing_accounts is the source of
        // truth) and send paid users straight to the management page. Works
        // even when the employer has no schools yet.
        const res = await subscription.getAccountStatus();
        const sStatus = res.data?.status;
        const sPlan = res.data?.plan;
        if (SUBSCRIBED_STATUSES.includes(sStatus) && sPlan && sPlan !== 'head_start') {
          navigate('/employer/subscription', { replace: true });
        }
      } catch (_e) {
        // Status unavailable — render the plans page as before.
      }
    };

    checkSubscription();
  }, [navigate]);

  // A plan picked on the public pricing page survives auth as ?plan=<backend_id>
  // (e.g. /employer/billing?plan=primary). Translate it back to the marketing
  // plan id the PricingSection cards are keyed by; ignore unknown values.
  const planParam = searchParams.get('plan');
  const preselectedPlanId =
    VALID_PLAN_IDS.includes(planParam) ? BACKEND_PLAN_ID_TO_MARKETING[planParam] : null;

  // The billing cycle chosen on the public pricing page (?cycle=annual).
  // Monthly is the default when the param is absent or unknown.
  const cycleParam = searchParams.get('cycle');
  const preselectedBillingCycle = isValidBillingCycle(cycleParam) ? cycleParam : 'monthly';

  const handleSelectPlan = async (planId, _plan, billingCycle) => {
    if (planId === 'head_start') {
      // Head Start is the default free plan — the management page (which works
      // school-less) shows its details and any upgrade options.
      navigate('/employer/subscription');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await subscription.createCheckout(
        planId,
        isValidBillingCycle(billingCycle) ? billingCycle : 'monthly',
        window.location.origin,
        '/employer/subscription'
      );
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header — matches EmployerDashboard slate theme */}
      <header className="bg-slate-900 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-slate-800"
            onClick={() => navigate('/employer')}
            data-testid="billing-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Link to="/employer" className="flex items-center gap-2">
            <BrandLogo variant="white" size="md" />
          </Link>
          <div className="ml-auto text-sm text-slate-300 hidden sm:block">
            Billing &amp; Plans
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <PlanUsageComparison />
        <PricingSection
          isAuthenticated={true}
          onSelectPlan={handleSelectPlan}
          preselectedPlanId={preselectedPlanId}
          preselectedBillingCycle={preselectedBillingCycle}
        />
      </main>
      {isSubmitting && (
        <div className="fixed inset-0 bg-slate-900/20 flex items-center justify-center z-50" data-testid="billing-checkout-overlay">
          <div className="bg-white rounded-lg px-6 py-4 shadow-lg text-slate-700 text-sm">
            Redirecting to checkout…
          </div>
        </div>
      )}
      <AppFooter role="employer" />
    </div>
  );
}
